import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";  
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearIcon from '@mui/icons-material/Clear';
import SelectAllIcon from '@mui/icons-material/SelectAll'; 
import NewBasicRangeShortcuts from "../date-range-picker/NewBasicRangeShortcuts";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function AnalyticsComparison() {
  // By default all sections are open
  const [openSections, setOpenSections] = useState({
    userEngagement: true,
    revenue: true,
    growthMetrics: true,
    communicationActivity: true,
    userSegmentsDemographics: true,
    platformDistribution: true,
    performanceQualityMetrics: true,
    marketingConversion: true,
  });
  const [showTables, setShowTables] = useState(false);

  // State for date ranges
  const [dateRange1, setDateRange1] = useState(null);
  const [dateRange2, setDateRange2] = useState(null);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const metricsList = [
    "User Engagement Metrics", 
    "Revenue & Monetization", 
    "Growth Metrics", 
    "Communication Activity", 
    "User Segments & Demographics", 
    "Platform Distribution", 
    "Performance & Quality Metrics", 
    "Marketing & Conversion", 
  ];

  const [selectedMetrics, setSelectedMetrics] = useState([]);

  const handleSelectMetric = (event, newValue) => {
    setSelectedMetrics(newValue);
  };

  const handleSelectAll = () => {
    setSelectedMetrics([...metricsList]);
  };

  const handleClearAll = () => {
    setSelectedMetrics([]);
  };

  const handleCompare = () => {
    setShowTables(true);
  };

  // Helper function to determine if value is positive/negative and extract numeric value
  const getValueInfo = (value) => {
    const isPositive = value.startsWith('+');
    const isNegative = value.startsWith('-');
    const numericValue = parseFloat(value.replace(/[+,%-]/g, ''));
    
    return { isPositive, isNegative, numericValue };
  };

  // Helper component for colored value with trend icon
  const ValueWithTrend = ({ value, fontSize }) => {
    const { isPositive, isNegative } = getValueInfo(value);
    
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        {isPositive && <ArrowUpwardIcon sx={{ color: "#00A63E", fontSize: 18 }} />}
        {isNegative && <ArrowDownwardIcon sx={{ color: "#EA4335", fontSize: 18 }} />}
        <Typography 
          variant="body2" 
          color={isPositive ? "#00A63E" : isNegative ? "#EA4335" : "textPrimary"}
          fontWeight="500"
          sx={{fontSize, my:0.5}}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  // Helper component for Trend column - only icon
  const TrendIconOnly = ({ value }) => {
    const { isPositive, isNegative } = getValueInfo(value);
    
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        {isPositive && <TrendingUpIcon sx={{ color: "#00A63E", fontSize: 24 }} />}
        {isNegative && <TrendingDownIcon sx={{ color: "#EA4335", fontSize: 24 }} />}
      </Box>
    );
  };

  const sections = [
    {
      key: "userEngagement",
      title: "User Engagement Metrics",
      metric: "User Engagement Metrics",
      rows: [
        ["User Engagement Metrics", "136,240.2", "154,778.1", "+18,537.9", "+13.6%"],
        ["Daily Active Users (DAU)", "12,450", "15,189", "+2,739", "22.0%"],
        ["Weekly Active Users (WAU)", "34,200", "41,040", "+6,840", "20.0%"],
        ["Monthly Active Users (MAU)", "89,500", "98,450", "+8,950", "10.0%"],
        ["Average Session Duration", "18 min", "22 min", "+4", "22.2%"],
        ["Sessions per User", "4.2", "5.1", "+0.9", "21.4%"],
        ["User Retention Rate", "68 %", "72 %", "+4", "5.9%"],
      ],
    },
    {
      key: "revenue",
      title: "Revenue & Monetization",
      metric: "Revenue & Monetization",
      rows: [
        ["Revenue & Monetization", "$179,752.21", "$142,046.67", "-$37,705.54", "-21.0%"],
        ["Total Revenue", "$89,750", "$70,922", "-$18,828", "-21.0%"],
        ["Premium Subscriptions", "$65,400", "$52,320", "-$13,080", "-20.0%"],
        ["In-app Purchases", "$18,200", "$13,104", "-$5,096", "-28.0%"],
        ["Gift Purchases", "$6,150", "$5,498", "-$652", "-10.6%"],
        ["Average Revenue per User (ARPU)", "$7.21", "$4.67", "-$2.54", "-35.2%"],
        ["Customer Lifetime Value (LTV)", "$245", "$198", "-$47", "-19.2%"],
      ],
    },
    {
      key: "growthMetrics",
      title: "Growth Metrics",
      metric: "Growth Metrics",
      rows: [
        ["Growth Metrics", "4,764.4", "5,704.2", "+939.8", "+19.7%"],
        ["New Signups", "2,340", "2,808", "+246", "+19.8%"],
        ["Organic Search (Source)", "1,240", "1,486", "+116", "+20.0%"],
        ["Paid Ads (Source)", "580", "696", "+68", "+20.0%"],
        ["Social Media (Source)", "340", "408", "+38", "+21.1%"],
        ["Activation Rate", "76 %", "82 %", "+6", "+7.9%"],
        ["Churn Rate", "8.4 %", "6.2 %", "-2.2", "-26.2%"],
      ],
    },
    {
      key: "communicationActivity",
      title: "Communication Activity",
      metric: "Communication Activity",
      rows: [
        ["Communication Activity", "312,612", "360,785", "+48,173", "+15.4%"],
        ["Total Chats Initiated", "145,670", "168,421", "+22,751", "+22.0%"],
        ["Total Messages Sent", "98,450", "113,784", "+15,334", "+22.0%"],
        ["Text Messages", "28,920", "33,474", "+4,554", "+20.0%"],
        ["Voice Messages", "18,300", "21,163", "+2,863", "+10.0%"],
        ["Media Files Shared", "8,920", "9,654", "+1,934", "+22.2%"],
        ["Video Calls Made", "12,340", "14,274", "+1,934", "+21.4%"],
        ["Audio Calls Made", "12,340", "14,274", "+1,934", "+5.9%"],
        ["Average Call Duration", "12 min", "15 min", "+4", "+5.9%"],
      ],
    },
    {
      key: "userSegmentsDemographics",
      title: "User Segments & Demographics",
      metric: "User Segments & Demographics",
      rows: [
        ["User Segments & Demographics", "312,612", "360,785", "+48,173", "+15.4%"],
        ["Free Tier Users", "12,450", "15,189", "+22,751", "+22.0%"],
        ["Gold Users", "34,200", "41,040", "+15,334", "+20.0%"],
        ["Luxury Users", "89,500", "98,450", "+4,554", "+10.0%"],
        ["Age 18 - 24", "18 min", "22 min", "+2,863", "+22.2%"],
        ["Age 25 - 34", "4.2", "5.1", "+734", "+21.4%"],
        ["Age 35 - 44", "68 %", "72 %", "+1,934", "+5.9%"],
        ["Age 45 - 70+", "68 %", "72 %", "+4", "+5.9%"],
      ],
    },
    {
      key: "platformDistribution",
      title: "Platform Distribution",
      metric: "Platform Distribution",
      rows: [
        ["Platform Distribution", "312,612", "360,785", "+48,173", "+15.4%"],
        ["iOS Users", "12,450", "15,189", "+2,739", "+22.0%"],
        ["Android Users", "34,200", "41,040", "+6,840", "+20.0%"],
        ["Web Users", "89,500", "98,450", "+8,950", "+10.0%"],
        ["iOS Revenue", "18 min", "22 min", "-30.1%", "-30.1%"],
        ["Android Revenue", "4.2", "5.1", "-30.1%", "-30.1%"],
        ["Web Revenue", "7,500", "5,489", "-2,011", "-26.8%"],
      ],
    },
    {
      key: "performanceQualityMetrics",
      title: "Performance & Quality Metrics",
      metric: "Performance & Quality Metrics",
      rows: [
        ["Performance & Quality Metrics", "576.4", "495.8", "-80.6", "+15.4%"],
        ["App Crash Rate", "0.8%", "0.4%", "-0.4%", "-50.0%"],
        ["Average Load Time", "2.4 sec", "1.8 sec", "-0.6", "-25.0%"],
        ["API Response Time", "185 ms", "142 ms", "-43", "-23.2%"],
        ["Support Tickets Created", "342", "289", "-53", "-15.5%"],
        ["Customer Satisfaction Score", "4.2/5", "4.6/5", "+0.4", "+9.5%"],
        ["Net Promoter Score (NPS)", "42", "58", "+16", "+38.1%"],
      ],
    },
    {
      key: "marketingConversion",
      title: "Marketing & Conversion",
      metric: "Marketing & Conversion",
      rows: [
        ["Marketing & Conversion", "1,389.7", "1,701", "+311.3", "+22.4%"],
        ["Gold Conversions", "456", "512", "+56", "+12.2%"],
        ["Luxury Conversions", "56", "12", "+44", "+12.3%"],
        ["Free to Gold Rate", "5.1 %", "4.8 %", "-0.3", "-5.9%"],
        ["Free to Luxury Rate", "5.1 %", "4.8 %", "-0.3", "-5.9%"],
        ["Gold to Luxury Rate", "5.1 %", "4.8 %", "-0.3", "-5.9%"],
        ["Email Click-through Rate", "18.4 %", "22.7 %", "+4.3", "+23.4%"],
        ["Push Notification Engagement", "28.2 %", "34.5 %", "+6.3", "+22.3%"],
        ["Landing Page Conversions", "840", "1,092", "+252", "+30.0%"],
        ["Cost per Acquisition (CPA)", "42", "35", "-7", "-16.7%"],
      ],
    }
  ];

  // Filter sections based on selected metrics
  const filteredSections = showTables 
    ? sections.filter(section => selectedMetrics.includes(section.metric))
    : [];

  return (
    <Box p={3}>
      <Box sx={{mb:3}}>
        <Typography sx={{color:'#000', fontWeight:'600', fontSize:'32px'}}>Metrics Comparison </Typography>
        <Typography sx={{fontSize:'16px', color:'#3E3E3E'}}>Compare two custom date ranges across multiple performance metrics.</Typography>
      </Box>
      {/* Date Range Selectors */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography sx={{fontSize:'14px',color:'#454545', fontWeight:'500', mb:0.5}}>
            Date Range 1
          </Typography>
          <NewBasicRangeShortcuts 
            id="range1" 
            onDateChange={setDateRange1}
          />
          {dateRange1 && (
            <Typography variant="caption" color="textSecondary">
              {dateRange1.startDate.format('MMM D, YYYY')} - {dateRange1.endDate.format('MMM D, YYYY')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={{fontSize:'14px',color:'#454545', fontWeight:'500', mb:0.5}}>
            Date Range 2
          </Typography>
          <NewBasicRangeShortcuts 
            id="range2" 
            onDateChange={setDateRange2}
          />
          {dateRange2 && (
            <Typography variant="caption" color="textSecondary">
              {dateRange2.startDate.format('MMM D, YYYY')} - {dateRange2.endDate.format('MMM D, YYYY')}
            </Typography>
          )}
        </Grid>
      </Grid> 
      
      <Box mt={3}>
       <Box>
         <Typography sx={{fontSize:'14px',color:'#454545', fontWeight:'500', mb:0.5}}>
          Metrics to Compare
        </Typography>
        
        {/* Select All / Clear All Buttons */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
            disabled={selectedMetrics.length === metricsList.length}
            sx={{ fontSize: '12px' }}
          >
            Select All
          </Button>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            disabled={selectedMetrics.length === 0}
            sx={{ fontSize: '12px' }}
          >
            Clear All
          </Button>
        </Stack>
       </Box>

        <Autocomplete
          multiple
          options={metricsList}
          value={selectedMetrics}
          onChange={handleSelectMetric} 
          disableCloseOnSelect
          popupIcon={<KeyboardArrowDownIcon />}
          getOptionLabel={(option) => option}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{fontSize:'14px', padding:'0'}}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select metrics..."
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={option}
                onDelete={() => setSelectedMetrics(prev => prev.filter(item => item !== option))}
              />
            ))
          }
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              right: '8px !important',
              top: '50%',
              transform: 'translateY(-50%)',
            }, 
            "& .MuiInputBase-root": {py:0},
            "& .MuiAutocomplete-inputRoot": {
              minHeight: "40px",
              maxHeight: "120px",
              overflowY: "auto",
              paddingTop: "4px",
              paddingBottom: "4px",
              alignItems: "flex-start !important",
            },
          }}
        />
      </Box> 
      
      <Box mt={2}>
        <Button 
          variant="contained"  
          sx={{background:'#916008'}}
          fullWidth
          onClick={handleCompare}
          disabled={selectedMetrics.length === 0}
        >
          Compare
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} my={2}>
        {[
          { label: "Total Active Users", value: "+22.0% (+2,739)", positive: true },
          { label: "Total Revenue", value: "-21.0% ($-18,828)", positive: false },
          { label: "New Signups", value: "+20.0% (+468)", positive: true },
          { label: "Premium Conversions", value: "+12.3% (+56)", positive: true },
        ].map((card, i) => (
          <Grid item xs={12} md={3} key={i}>
            <Card sx={{boxShadow:'none',  border: i === 1 ? "1px solid #ff4A4A" : "1px solid #E8E8E8",  background: i === 1 ? "#f7f3ec" : "#fff", borderRadius:'16px'}}>
              <CardContent>
                <Typography sx={{fontSize:'14px', textTransform:'uppercase', color:'#757575', fontWeight:'500',}}>{card.label}</Typography>
                <ValueWithTrend value={card.value} fontSize="20px" />
                <Box sx={{mt:1}}>
                  <Typography sx={{fontSize:'12px', textTransform:'capitalize', color:'#757575', fontWeight:'500', display:'flex', justifyContent:'space-between', alignItems:'center', mb:1}}>Date Range 1 <Typography variant="span" sx={{color:'#000'}}>12,450</Typography></Typography> 
                  <Typography sx={{fontSize:'12px', textTransform:'capitalize', color:'#757575', fontWeight:'500', mb:0, display:'flex', justifyContent:'space-between', alignItems:'center'}}>Date Range 2 <Typography variant="span" sx={{color:'#000'}}>15,189</Typography></Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detailed Sections - Only show when Compare is clicked and metrics are selected */}
      {showTables && filteredSections.map((section) => (
        <Box key={section.key} mt={0}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            onClick={() => toggleSection(section.key)}
            sx={{ cursor: "pointer", mb: 0, py:1.5, '&:hover':{color:'#916008'}, borderBottom:'1px solid #ccc',  }}
          >
            <Typography sx={{fontSize:'20px',}}>{section.title}</Typography>
            <IconButton>
              {openSections[section.key] ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </Box>

          {openSections[section.key] && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell>Range 1</TableCell>
                  <TableCell>Range 2</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>% Change</TableCell>
                  <TableCell sx={{textAlign:'center'}}>Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.rows.map((r, i) => (
                  <TableRow key={i} sx={{background: i === 0 ? "rgba(191, 133, 0, 0.1)" : "#fff"}}>
                    <TableCell sx={{ py: 1 }}><Box sx={{display:"flex", alignItems:'center', gap:1, color: i === 0 ? '#916008' : "#3E3E3E"}}><TrendingUpIcon value={r[3]} sx={{display: i === 0 ? "inline" : "none"}} /> {r[0]}</Box></TableCell> 
                    <TableCell sx={{ py: 1 }}>{r[1]}</TableCell>
                    <TableCell sx={{ py: 1 }}>{r[2]}</TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <ValueWithTrend value={r[3]} />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <ValueWithTrend value={r[4]} />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <TrendIconOnly value={r[3]} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      ))}
    </Box>
  );
}